import axios from 'axios';
import get from 'lodash/get.js';
import merge from 'lodash/merge.js';

import { handleResponseError } from '@/Helpers.js';
import Course from '@/models/Course.js';
import Collection from '@/store/quill/Collection.js';
import EdverumTable from '@/store/utilities/EdverumTable.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

const collectionHelpers = EdverumTable.collection('notes');

export default merge(Collection('NoteDrawer'), {
    state: {
        showNoteDrawer: false,
        showNoteForm: false,
        body: null,
        id: null,
        title: null,
        uuid: null,
        updated_at: null,
        created_at: null,
        activity_id: null,
        course_id: null,
        tags: null,
        media: [],
        key: 0,
        isNewNote: true,
        refreshKey: 0,
        isSaving: false,
        refreshTags: false,
        courseTitle: null,
        currentNote: null,
        isLoading: false,
        hasChanges: false,
        lastSavedState: null,
        saveTimeout: null,
        course: null,
        isCreatingNote: false,
        closePromiseResolver: null, // Promise resolver for coordinating drawer close with save
        shouldSaveBeforeClose: false, // Flag to trigger save before closing (used by closeNoteDrawerWithSave)
    },

    actions: {
        async hydrateNote(context, payload) {
            const data = {
                note: payload,
                showNoteDrawer: true,
                isNewNote: false,
            };
            context.commit('UPDATE_NOTE_DRAWER', data);
            context.commit('SET_INITIAL_LOAD', false);
        },

        async hydrateNoteWithTags(context, payload) {
            context.commit('UPDATE_NOTE_DRAWER_TAGS', payload);
        },

        async editField({ commit, state, dispatch }, { field, value }) {
            if (state.saveTimeout) {
                clearTimeout(state.saveTimeout);
            }

            if (!state.currentNote) {
                state.currentNote = {};
            }

            if (field === 'body') {
                commit('EDIT_FIELD', { field, value });

                state.saveTimeout = setTimeout(() => {
                    if (state.currentNote?.id) {
                        dispatch('autoSave');
                    }
                }, 1000);
                return;
            }

            const currentValue = state.currentNote[field];
            if (currentValue === value) {
                return;
            }

            commit('EDIT_FIELD', { field, value });

            if (field === 'course_id') {
                if (state.currentNote?.id) {
                    await dispatch('autoSave');
                }
                return;
            }

            state.saveTimeout = setTimeout(() => {
                if (state.currentNote?.id && state.hasChanges) {
                    dispatch('autoSave');
                }
            }, 1000);
        },

        stageField(context, payload) {
            context.commit('EDIT_FIELD', payload);
        },

        fetch(context, payload) {
            const queryBuilder = new QueryBuilder(`notes/${payload}`);
            queryBuilder.include(['tags']);
        },

        updateTags: collectionHelpers.actions.updateNoteTags(),

        async updateNote({ commit, dispatch }, { id, data }) {
            try {
                const response = await axios.put(`/api/notes/${id}`, data);

                if (response.data) {
                    await dispatch('fetchOne', id);
                }

                return response.data;
            } catch (error) {
                console.error('[NoteDrawer] updateNote - error:', error);
                throw error;
            }
        },

        async createNote({ commit, dispatch, state }, noteData) {
            // Guard against race condition - only allow one note creation at a time
            if (state.isCreatingNote) {
                console.warn('[NoteDrawer] Note creation already in progress, ignoring duplicate request');
                return null;
            }

            commit('SET_CREATING_NOTE', true);
            try {
                const response = await axios.post('/api/notes', noteData);
                const newNoteId = response.data.data.id;
                await dispatch('fetchOne', newNoteId);
                const Note = (await import('@/models/Note.js')).default;
                const ormNote = Note.query()
                    .with('tags')
                    .with('course')
                    .with('activity')
                    .with('media')
                    .where('id', newNoteId)
                    .first();
                commit('UPDATE_NOTE_DRAWER', {
                    showNoteDrawer: state.showNoteDrawer,
                    isNewNote: false,
                    note: ormNote,
                    courseTitle: state.courseTitle,
                });
                return ormNote;
            } finally {
                commit('SET_CREATING_NOTE', false);
            }
        },

        refreshTable(context) {
            // Note: Deprecated - use VuexORM reactivity instead of forced refreshes
            console.warn('[NoteDrawer] refreshTable is deprecated - VuexORM handles reactivity automatically');
        },

        async noteSaved({ dispatch }) {
            // Just dispatch a lightweight custom event for table refresh
            window.dispatchEvent(new CustomEvent('notes-table-refresh'));
            return true;
        },

        async handleRefreshData({ state, commit, emit }, { model, queryBuilder, route }) {
            try {
                if (!state.refreshQuietly) {
                }

                const apiResponse = await model.api().get(queryBuilder.toUrl(), {
                    dataTransformer: apiData => {
                        if (!apiData.data) {
                            return { data: [] };
                        }

                        if (!Array.isArray(apiData.data)) {
                            return {
                                ...apiData,
                                data: [apiData.data],
                            };
                        }

                        return apiData;
                    },
                });

                if (get(apiResponse, 'response.data.meta.course')) {
                    await Course.insert({ data: apiResponse.response.data.meta.course });
                }

                emit('after-retrieve', {
                    response: apiResponse.response,
                    queryBuilder,
                });

                commit('UPDATE_PAGINATE_META', {
                    ...state.paginateMeta,
                    ...(apiResponse?.response?.data?.meta || {}),
                    total: apiResponse?.response?.data?.meta?.total || 0,
                    current_page: apiResponse?.response?.data?.meta?.current_page || 1,
                    per_page: apiResponse?.response?.data?.meta?.per_page || state.perPage,
                });

                return true;
            } catch (error) {
                handleResponseError(error);
                return false;
            }
        },

        async autoSave({ state, commit }) {
            if (!state.currentNote?.id || !state.hasChanges) {
                return;
            }

            commit('SET_SAVING', true);
            try {
                // Tags are intentionally omitted. Tags are owned exclusively by the dedicated
                // `/notes/{id}/tags` endpoint (TagSwitcher autosave). state.currentNote.tags is
                // NOT kept in sync with those edits, so including it here would full-replace the
                // backend tags with a stale array and wipe tags the user just added (868fzvgdx).
                // The backend leaves tags untouched when `tags` is absent from the payload.
                await axios.put(`/api/notes/${state.currentNote.id}`, {
                    title: state.currentNote.title,
                    body: state.currentNote.body,
                    course_id: state.currentNote.course_id,
                    activity_id: state.currentNote.activity_id,
                });

                commit('SET_SAVING', false);
                commit('SET_HAS_CHANGES', false);
            } catch (_error) {
                commit('SET_SAVING', false);
            }
        },

        async deleteMedia({ commit, state }, { media }) {
            if (!state.currentNote?.id || !media?.id) {
                return;
            }

            try {
                await axios.delete(`/api/notes/${state.currentNote.id}/media`, {
                    data: {
                        delete_media: [media.id],
                    },
                });

                commit('DELETE_MEDIA', { media });

                // Reload the note to ensure media is properly synchronized
                if (state.currentNote.id) {
                    const response = await axios.get(
                        `/api/notes/${state.currentNote.id}?include=tags,media,activity,course`
                    );
                    const noteData = response.data?.data || response.data;

                    if (noteData && noteData.media) {
                        commit('EDIT_FIELD', { field: 'media', value: noteData.media });
                    }
                }
            } catch (error) {
                console.error('Error deleting media:', error);
                // Optionally show error message to user
                throw error;
            }
        },

        async fetchOne({ commit, state }, id) {
            try {
                const response = await axios.get(`/api/notes/${id}?include=tags,media,activity,course`);
                const noteData = response.data;

                const actualNoteData = noteData.data || noteData;

                if (actualNoteData) {
                    const Note = (await import('@/models/Note.js')).default;
                    const Taggable = (await import('@/models/Taggable.js')).default;
                    const Tag = (await import('@/models/Tag.js')).default;

                    Note.delete(id);
                    Taggable.query()
                        .where('taggable_id', id)
                        .get()
                        .forEach(row => Taggable.delete(row.$id));

                    const existingNote = Note.query().where('id', id).with('tags').first();
                    if (existingNote && existingNote.tags) {
                        existingNote.tags.forEach(tag => Tag.delete(tag.id));
                    }

                    Note.insert({ data: actualNoteData });

                    const ormNote = Note.query()
                        .with('tags')
                        .with('course')
                        .with('activity')
                        .with('media')
                        .where('id', id)
                        .first();

                    commit('UPDATE_NOTE_DRAWER', {
                        showNoteDrawer: state.showNoteDrawer,
                        isNewNote: false,
                        note: ormNote,
                        courseTitle: state.courseTitle,
                    });

                    return ormNote;
                }
            } catch (error) {
                console.error('[NoteDrawer] fetchOne - error:', error);
                throw error;
            }
        },

        /**
         * Close the note drawer and wait for any pending saves to complete.
         * Sets a flag that NoteDrawer component watches to trigger saveIfNeeded() before closing.
         * Used to prevent race conditions when deleting a note that's currently open.
         */
        async closeNoteDrawerWithSave({ commit, state }) {
            // Create a promise that will be resolved when the save completes
            const savePromise = new Promise(resolve => {
                commit('SET_CLOSE_PROMISE_RESOLVER', resolve);
            });

            // Set flag to trigger save before close (NoteDrawer component will watch this)
            commit('SET_SHOULD_SAVE_BEFORE_CLOSE', true);

            // Wait for save to complete and call the resolver
            // Or timeout after 5 seconds to prevent hanging
            const timeoutPromise = new Promise(resolve => setTimeout(resolve, 5000));
            await Promise.race([savePromise, timeoutPromise]);

            // Clear the resolver and flag
            commit('SET_CLOSE_PROMISE_RESOLVER', null);
            commit('SET_SHOULD_SAVE_BEFORE_CLOSE', false);
        },

        /**
         * Signal that the note save is complete.
         * Called by NoteForm component after beforeUnmount finishes saving.
         */
        notifyDrawerSaveComplete({ state }) {
            if (state.closePromiseResolver) {
                state.closePromiseResolver();
            }
        },
    },

    getters: {
        note: state =>
            state.currentNote || {
                id: null,
                title: null,
                body: null,
                course_id: null,
                course: null,
                activity_id: null,
                activity: null,
                created_at: null,
                updated_at: null,
                uuid: null,
                media: [],
                tags: null,
            },
        noteDrawer: state => {
            return state.showNoteDrawer;
        },
        noteId: state => {
            return state.currentNote?.id || state.id;
        },
        key: state => {
            return state.key;
        },
        noteForm: state => {
            return state.showNoteForm;
        },
        isNewNote: state => {
            return state.isNewNote;
        },
        tags: state => {
            return state.tags;
        },
        courseId: state => {
            return state.course_id;
        },
        activityId: state => {
            return state.activity_id;
        },
        isSaving: state => {
            return state.isSaving;
        },
        courseTitle: state => {
            return state.courseTitle;
        },
        course: state => {
            return state.course;
        },
    },

    mutations: {
        UPDATE_NOTE_DRAWER(state, payload) {
            state.showNoteDrawer = payload.showNoteDrawer;
            state.isNewNote = payload.isNewNote;
            if (payload.note && Array.isArray(payload.note.tags)) {
                const seen = new Set();
                payload.note.tags = payload.note.tags.filter(tag => {
                    if (seen.has(tag.id)) return false;
                    seen.add(tag.id);
                    return true;
                });
            }
            state.currentNote = payload.note;
            state.courseTitle = payload.courseTitle;
            state.lastSavedState = payload.note ? JSON.stringify(payload.note) : null;
            state.hasChanges = false;
            state.isInitialLoad = false;
            state.course = payload.note?.course || null;
            if (payload.isNewNote) {
                state.id = null;
            }
        },

        UPDATE_NOTE_DRAWER_TAGS(state, payload) {
            state.tags = payload;
        },

        CLOSE_NOTE_DRAWER(state) {
            // Clear any pending save timeout to prevent stale saves
            if (state.saveTimeout) {
                clearTimeout(state.saveTimeout);
                state.saveTimeout = null;
            }

            state.showNoteDrawer = false;
            state.showNoteForm = false;
            state.currentNote = null;
            state.id = null;
            state.hasChanges = false;
            state.key += 1;
        },

        SHOW_NOTE_FORM(state) {
            state.showNoteForm = true;
        },

        UPDATE_NOTE_ID(state, payload) {
            state.id = payload.id;
        },

        EDIT_FIELD(state, payload) {
            if (!state.currentNote) {
                state.currentNote = {};
            }

            if (payload.field === 'body') {
                state.currentNote.body = payload.value;
                state.body = payload.value;
            } else if (payload.field === 'course_id') {
                const value = payload.value ? Number(payload.value) : null;
                state.currentNote.course_id = value;
                state.course_id = value;
            } else {
                state.currentNote[payload.field] = payload.value;
                state[payload.field] = payload.value;
            }

            state.hasChanges = true;
        },
        UPDATE_TAGS(state, { tags }) {
            state.tags = tags;
        },

        UPDATE_COURSE_ID(state, payload) {
            state.course_id = payload;
        },

        UPDATE_COURSE_TITLE(state, payload) {
            state.courseTitle = payload;
        },

        SET_SAVING(state, payload) {
            state.isSaving = payload;
        },

        SET_NEW_NOTE(state) {
            state.isNewNote = false;
        },

        DELETE_MEDIA(state, payload) {
            if (!state.currentNote) {
                state.currentNote = {};
            }

            // Remove media from state arrays
            if (state.media && Array.isArray(state.media)) {
                state.media = state.media.filter(media => media.id !== payload.media.id);
            }

            if (state.currentNote.media && Array.isArray(state.currentNote.media)) {
                state.currentNote.media = state.currentNote.media.filter(media => media.id !== payload.media.id);
            }

            state.hasChanges = true;
        },

        NOTE_SAVED(state) {
            state.isSaving = false;
            state.hasChanges = false;
            state.lastSavedState = JSON.stringify(state.currentNote);
            if (state.saveTimeout) {
                clearTimeout(state.saveTimeout);
                state.saveTimeout = null;
            }
        },

        SET_LOADING(state, value) {
            state.isLoading = value;
        },

        SET_HAS_CHANGES(state, value) {
            state.hasChanges = value;
        },

        INCREMENT_KEY(state) {
            state.key += 1;
        },

        AUTO_SAVE(state) {
            state.isSaving = true;
        },

        SET_INITIAL_LOAD(state, value) {
            state.isInitialLoad = value;
        },

        SET_CREATING_NOTE(state, value) {
            state.isCreatingNote = value;
        },

        SET_CLOSE_PROMISE_RESOLVER(state, resolver) {
            state.closePromiseResolver = resolver;
        },

        SET_SHOULD_SAVE_BEFORE_CLOSE(state, value) {
            state.shouldSaveBeforeClose = value;
        },
    },
});
